/**
 * Inventory Service
 *
 * Vision: Effortless awareness - the system thinks ahead so you don't have to.
 * Spend less time worrying about inventory and more time cutting hair.
 *
 * This service manages:
 * - Products with real-time sync
 * - Vendors with contact info
 * - Automatic replenishment queue
 * - Purchase order workflow
 * - Transaction audit trail
 * - Inventory count sessions
 * - Push notifications for stock alerts
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  getDoc,
  Timestamp,
  serverTimestamp,
  writeBatch,
  increment,
  QueryConstraint
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import {
  Product,
  ProductFormData,
  ProductFilterParams,
  ProductWithStatus,
  Vendor,
  VendorFormData,
  VendorWithStats,
  InventoryTransaction,
  InventoryTransactionType,
  TransactionFilterOptions,
  ReplenishmentQueueItem,
  ReplenishmentTrigger,
  ReplenishmentStatus,
  VendorQueueGroup,
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderFormData,
  PurchaseOrderStatus,
  InventoryCountSession,
  InventoryCountItem,
  CountSessionStatus,
  VarianceReport,
  VarianceDiscrepancy,
  InventoryStats,
  InventoryNotification,
  NotificationPriority,
  ProductCategory,
  getStockLevelStatus,
  calculateSuggestedOrderQuantity,
  generateOrderNumber,
  INVENTORY_COLLECTIONS
} from '../types/inventory';

// Use collection names from types
const COLLECTIONS = INVENTORY_COLLECTIONS;

/**
 * InventoryService Class
 *
 * Singleton service providing comprehensive inventory management.
 * Features real-time Firebase listeners, caching, and automatic replenishment.
 */
class InventoryService {
  // ============================================================================
  // INTERNAL STATE & CACHING
  // ============================================================================

  private productSubscribers: Array<(products: Product[]) => void> = [];
  private vendorSubscribers: Array<(vendors: Vendor[]) => void> = [];
  private queueSubscribers: Array<(items: ReplenishmentQueueItem[]) => void> = [];
  private statsSubscribers: Array<(stats: InventoryStats) => void> = [];

  private cachedProducts: Product[] = [];
  private cachedVendors: Vendor[] = [];
  private cachedQueueItems: ReplenishmentQueueItem[] = [];

  private unsubscribeProducts: (() => void) | null = null;
  private unsubscribeVendors: (() => void) | null = null;
  private unsubscribeQueue: (() => void) | null = null;

  private isInitialized = false;
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.initPromise = this.initializeListeners();
    // RESILIENCE (Jose 2026-10-29 audit): subscribes at module load (pre-auth) → re-subscribe once
    // auth is ready so a denied cold read self-heals (unsub prior first). See shiftService fix.
    try { onAuthStateChanged(auth, (u) => { if (u) this.initPromise = this.initializeListeners(); }); } catch { /* ignore */ }
  }

  /**
   * Wait for service to be fully initialized
   */
  async ready(): Promise<void> {
    if (this.initPromise) {
      await this.initPromise;
    }
  }

  // ============================================================================
  // INITIALIZATION & REAL-TIME LISTENERS
  // ============================================================================

  /**
   * Initialize real-time listeners for products, vendors, and queue
   */
  private async initializeListeners(): Promise<void> {
    try {
      // Tear down any prior listeners first so an auth-ready re-subscribe never stacks duplicates.
      try { this.unsubscribeProducts?.(); } catch { /* ignore */ }
      try { this.unsubscribeVendors?.(); } catch { /* ignore */ }
      try { this.unsubscribeQueue?.(); } catch { /* ignore */ }
      this.unsubscribeProducts = this.unsubscribeVendors = this.unsubscribeQueue = null;

      // Listen to products collection
      const productsQuery = query(
        collection(db, COLLECTIONS.PRODUCTS),
        orderBy('name', 'asc')
      );

      this.unsubscribeProducts = onSnapshot(productsQuery, (snapshot) => {
        this.cachedProducts = snapshot.docs.map(docSnap => this.mapProductDoc(docSnap));
        this.notifyProductSubscribers();
        this.notifyStatsSubscribers();
      }, (error) => {
        console.error('Error listening to inventory products:', error);
      });

      // Listen to vendors collection
      const vendorsQuery = query(
        collection(db, COLLECTIONS.VENDORS),
        orderBy('name', 'asc')
      );

      this.unsubscribeVendors = onSnapshot(vendorsQuery, (snapshot) => {
        this.cachedVendors = snapshot.docs.map(docSnap => this.mapVendorDoc(docSnap));
        this.notifyVendorSubscribers();
        this.notifyStatsSubscribers();
      }, (error) => {
        console.error('Error listening to inventory vendors:', error);
      });

      // Listen to replenishment queue
      const queueQuery = query(
        collection(db, COLLECTIONS.QUEUE),
        where('status', '==', 'queued'),
        orderBy('triggeredAt', 'asc')
      );

      this.unsubscribeQueue = onSnapshot(queueQuery, (snapshot) => {
        this.cachedQueueItems = snapshot.docs.map(docSnap => this.mapQueueItemDoc(docSnap));
        this.notifyQueueSubscribers();
        this.notifyStatsSubscribers();
      }, (error) => {
        console.error('Error listening to replenishment queue:', error);
      });

      this.isInitialized = true;
      console.log('✓ Inventory service initialized with real-time listeners');

    } catch (error) {
      console.error('Error initializing inventory listeners:', error);
      throw error;
    }
  }

  // ============================================================================
  // DOCUMENT MAPPERS
  // ============================================================================

  private mapProductDoc(docSnap: any): Product {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name || '',
      barcode: data.barcode || undefined,
      sku: data.sku || undefined,
      category: data.category || 'other',
      vendorId: data.vendorId || '',
      vendorName: data.vendorName || '',
      currentQuantity: data.currentQuantity ?? 0,
      parLevel: data.parLevel ?? 5,
      criticalLevel: data.criticalLevel ?? 2,
      reorderQuantity: data.reorderQuantity ?? 10,
      unitCost: data.unitCost ?? 0,
      retailPrice: data.retailPrice || undefined,
      imageUrl: data.imageUrl || undefined,
      description: data.description || undefined,
      isActive: data.isActive !== false,
      lastCountedAt: data.lastCountedAt?.toDate() || undefined,
      lastOrderedAt: data.lastOrderedAt?.toDate() || undefined,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  }

  private mapVendorDoc(docSnap: any): Vendor {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name || '',
      type: data.type || 'product',
      contactName: data.contactName || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
      website: data.website || undefined,
      address: data.address || undefined,
      accountUsername: data.accountUsername || undefined,
      accountPassword: data.accountPassword || undefined,
      leadTimeDays: data.leadTimeDays ?? 7,
      minimumOrder: data.minimumOrder || undefined,
      productCount: data.productCount ?? 0,
      pendingOrdersCount: data.pendingOrdersCount ?? 0,
      notes: data.notes || undefined,
      isActive: data.isActive !== false,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  }

  private mapQueueItemDoc(docSnap: any): ReplenishmentQueueItem {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      productId: data.productId || '',
      productName: data.productName || '',
      productBarcode: data.productBarcode || undefined,
      vendorId: data.vendorId || '',
      vendorName: data.vendorName || '',
      currentQuantity: data.currentQuantity ?? 0,
      parLevel: data.parLevel ?? 0,
      suggestedOrderQuantity: data.suggestedOrderQuantity ?? 0,
      unitCost: data.unitCost ?? 0,
      estimatedCost: data.estimatedCost ?? 0,
      triggeredAt: data.triggeredAt?.toDate() || new Date(),
      triggeredBy: data.triggeredBy || 'manual',
      status: data.status || 'queued',
      purchaseOrderId: data.purchaseOrderId || undefined,
      notes: data.notes || undefined
    };
  }

  private mapTransactionDoc(docSnap: any): InventoryTransaction {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      productId: data.productId || '',
      productName: data.productName || '',
      productBarcode: data.productBarcode || undefined,
      type: data.type || 'adjustment',
      quantity: data.quantity ?? data.quantityChange ?? 0,
      quantityChange: data.quantityChange ?? data.quantity ?? 0,
      previousQuantity: data.previousQuantity ?? 0,
      newQuantity: data.newQuantity ?? 0,
      referenceId: data.referenceId || undefined,
      referenceType: data.referenceType || undefined,
      reason: data.reason || undefined,
      notes: data.notes || undefined,
      createdBy: data.createdBy || data.userId || '',
      userId: data.userId || data.createdBy || '',
      userName: data.userName || 'Unknown',
      createdAt: data.createdAt?.toDate() || new Date()
    };
  }

  private mapPurchaseOrderDoc(docSnap: any): PurchaseOrder {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      orderNumber: data.orderNumber || '',
      vendorId: data.vendorId || '',
      vendorName: data.vendorName || '',
      items: (data.items || []).map((item: any) => ({
        id: item.id || '',
        productId: item.productId || '',
        productName: item.productName || '',
        productBarcode: item.productBarcode || undefined,
        quantityOrdered: item.quantityOrdered ?? 0,
        quantityReceived: item.quantityReceived ?? 0,
        unitCost: item.unitCost ?? 0,
        totalCost: item.totalCost ?? 0,
        isReceived: item.isReceived ?? false
      })),
      totalCost: data.totalCost ?? 0,
      status: data.status || 'draft',
      createdAt: data.createdAt?.toDate() || new Date(),
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      approvedAt: data.approvedAt?.toDate() || undefined,
      approvedBy: data.approvedBy || undefined,
      approvedByName: data.approvedByName || undefined,
      orderedAt: data.orderedAt?.toDate() || undefined,
      orderMethod: data.orderMethod || 'manual',
      expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || undefined,
      receivedAt: data.receivedAt?.toDate() || undefined,
      notes: data.notes || undefined,
      orderConfirmation: data.orderConfirmation || undefined
    };
  }

  private mapCountSessionDoc(docSnap: any): InventoryCountSession {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      status: data.status || 'in_progress',
      startedAt: data.startedAt?.toDate() || new Date(),
      startedBy: data.startedBy || '',
      startedByName: data.startedByName || '',
      completedAt: data.completedAt?.toDate() || undefined,
      completedBy: data.completedBy || undefined,
      itemsExpected: data.itemsExpected ?? 0,
      itemsCounted: data.itemsCounted ?? 0,
      items: data.items || [],
      varianceReport: data.varianceReport || undefined,
      notes: data.notes || undefined
    };
  }

  // ============================================================================
  // PRODUCT CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new product
   */
  async createProduct(data: ProductFormData): Promise<Product> {
    // Get vendor name for denormalization
    const vendor = this.cachedVendors.find(v => v.id === data.vendorId);
    const vendorName = vendor?.name || 'Unknown Vendor';

    const productData = {
      ...data,
      vendorName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
    console.log('✓ Product created:', data.name);

    // Update vendor product count
    if (data.vendorId) {
      await this.updateVendorProductCount(data.vendorId, 1);
    }

    // Check if below par level immediately
    if (data.currentQuantity <= data.parLevel) {
      const newProduct: Product = {
        id: docRef.id,
        ...data,
        vendorName,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await this.addToReplenishmentQueue(newProduct, 'par_alert');
    }

    return {
      id: docRef.id,
      ...data,
      vendorName,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  /**
   * Update an existing product
   */
  async updateProduct(id: string, data: Partial<ProductFormData>): Promise<void> {
    const updateData: any = {
      ...data,
      updatedAt: serverTimestamp()
    };

    // If vendor changed, update vendor name
    if (data.vendorId) {
      const vendor = this.cachedVendors.find(v => v.id === data.vendorId);
      updateData.vendorName = vendor?.name || 'Unknown Vendor';

      // Update old vendor's product count
      const oldProduct = this.cachedProducts.find(p => p.id === id);
      if (oldProduct && oldProduct.vendorId !== data.vendorId) {
        await this.updateVendorProductCount(oldProduct.vendorId, -1);
        await this.updateVendorProductCount(data.vendorId, 1);
      }
    }

    await updateDoc(doc(db, COLLECTIONS.PRODUCTS, id), updateData);
    console.log('✓ Product updated:', id);
  }

  /**
   * Delete a product (soft delete by default)
   */
  async deleteProduct(id: string, hard = false): Promise<void> {
    const product = this.cachedProducts.find(p => p.id === id);

    if (hard) {
      await deleteDoc(doc(db, COLLECTIONS.PRODUCTS, id));
    } else {
      await updateDoc(doc(db, COLLECTIONS.PRODUCTS, id), {
        isActive: false,
        updatedAt: serverTimestamp()
      });
    }

    // Update vendor product count
    if (product?.vendorId) {
      await this.updateVendorProductCount(product.vendorId, -1);
    }

    console.log('✓ Product deleted:', id);
  }

  /**
   * Get a single product by ID
   */
  async getProduct(id: string): Promise<Product | null> {
    // Try cache first
    const cached = this.cachedProducts.find(p => p.id === id);
    if (cached) return cached;

    // Fetch from Firebase
    const docSnap = await getDoc(doc(db, COLLECTIONS.PRODUCTS, id));
    if (!docSnap.exists()) return null;

    return this.mapProductDoc(docSnap);
  }

  /**
   * Get product by barcode (fast lookup)
   */
  async getProductByBarcode(barcode: string): Promise<Product | null> {
    // Try cache first for instant response
    const cached = this.cachedProducts.find(p => p.barcode === barcode && p.isActive);
    if (cached) return cached;

    // Fall back to Firebase query
    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('barcode', '==', barcode),
      where('isActive', '==', true),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    return this.mapProductDoc(snapshot.docs[0]);
  }

  /**
   * Get product by SKU
   */
  async getProductBySku(sku: string): Promise<Product | null> {
    const cached = this.cachedProducts.find(p => p.sku === sku && p.isActive);
    if (cached) return cached;

    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('sku', '==', sku),
      where('isActive', '==', true),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    return this.mapProductDoc(snapshot.docs[0]);
  }

  /**
   * Get all active products (from cache)
   */
  getAllProducts(): Product[] {
    return this.cachedProducts.filter(p => p.isActive);
  }

  /**
   * Get products with computed stock status
   */
  getProductsWithStatus(): ProductWithStatus[] {
    return this.cachedProducts
      .filter(p => p.isActive)
      .map(product => ({
        ...product,
        stockStatus: getStockLevelStatus(
          product.currentQuantity,
          product.parLevel,
          product.criticalLevel
        )
      }));
  }

  /**
   * Get products filtered by various criteria
   */
  getFilteredProducts(filters: ProductFilterParams): ProductWithStatus[] {
    let products = this.getProductsWithStatus();

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      products = products.filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        p.barcode?.toLowerCase().includes(searchLower) ||
        p.sku?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.category) {
      products = products.filter(p => p.category === filters.category);
    }

    if (filters.vendorId) {
      products = products.filter(p => p.vendorId === filters.vendorId);
    }

    if (filters.stockStatus) {
      products = products.filter(p => p.stockStatus === filters.stockStatus);
    }

    if (filters.isActive !== undefined) {
      products = products.filter(p => p.isActive === filters.isActive);
    }

    return products;
  }

  /**
   * Get low stock products (below par level)
   */
  getLowStockProducts(): ProductWithStatus[] {
    return this.getProductsWithStatus().filter(
      p => p.stockStatus === 'low' || p.stockStatus === 'critical' || p.stockStatus === 'out_of_stock'
    );
  }

  /**
   * Get critical stock products (below critical level)
   */
  getCriticalStockProducts(): ProductWithStatus[] {
    return this.getProductsWithStatus().filter(
      p => p.stockStatus === 'critical' || p.stockStatus === 'out_of_stock'
    );
  }

  /**
   * Get out of stock products
   */
  getOutOfStockProducts(): ProductWithStatus[] {
    return this.getProductsWithStatus().filter(p => p.stockStatus === 'out_of_stock');
  }

  /**
   * Get products by vendor
   */
  getProductsByVendor(vendorId: string): Product[] {
    return this.cachedProducts.filter(p => p.vendorId === vendorId && p.isActive);
  }

  /**
   * Get products by category
   */
  getProductsByCategory(category: ProductCategory): Product[] {
    return this.cachedProducts.filter(p => p.category === category && p.isActive);
  }

  // ============================================================================
  // VENDOR CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new vendor
   */
  async createVendor(data: VendorFormData): Promise<Vendor> {
    const vendorData = {
      ...data,
      productCount: 0,
      pendingOrdersCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.VENDORS), vendorData);
    console.log('✓ Vendor created:', data.name);

    return {
      id: docRef.id,
      ...data,
      productCount: 0,
      pendingOrdersCount: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  /**
   * Update an existing vendor
   */
  async updateVendor(id: string, data: Partial<VendorFormData>): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.VENDORS, id), {
      ...data,
      updatedAt: serverTimestamp()
    });

    // If vendor name changed, update all products with this vendor
    if (data.name) {
      const productsToUpdate = this.cachedProducts.filter(p => p.vendorId === id);
      const batch = writeBatch(db);

      productsToUpdate.forEach(product => {
        batch.update(doc(db, COLLECTIONS.PRODUCTS, product.id), {
          vendorName: data.name,
          updatedAt: serverTimestamp()
        });
      });

      if (productsToUpdate.length > 0) {
        await batch.commit();
      }
    }

    console.log('✓ Vendor updated:', id);
  }

  /**
   * Delete a vendor (soft delete by default)
   */
  async deleteVendor(id: string, hard = false): Promise<void> {
    // Check if vendor has products
    const products = this.getProductsByVendor(id);
    if (products.length > 0 && hard) {
      throw new Error('Cannot hard delete vendor with existing products');
    }

    if (hard) {
      await deleteDoc(doc(db, COLLECTIONS.VENDORS, id));
    } else {
      await updateDoc(doc(db, COLLECTIONS.VENDORS, id), {
        isActive: false,
        updatedAt: serverTimestamp()
      });
    }

    console.log('✓ Vendor deleted:', id);
  }

  /**
   * Get a single vendor by ID
   */
  async getVendor(id: string): Promise<Vendor | null> {
    const cached = this.cachedVendors.find(v => v.id === id);
    if (cached) return cached;

    const docSnap = await getDoc(doc(db, COLLECTIONS.VENDORS, id));
    if (!docSnap.exists()) return null;

    return this.mapVendorDoc(docSnap);
  }

  /**
   * Get all active vendors (from cache)
   */
  getAllVendors(): Vendor[] {
    return this.cachedVendors.filter(v => v.isActive);
  }

  /**
   * Get vendors with aggregated stats
   */
  getVendorsWithStats(): VendorWithStats[] {
    return this.getAllVendors().map(vendor => {
      const products = this.getProductsByVendor(vendor.id);
      const totalProductValue = products.reduce(
        (sum, p) => sum + (p.currentQuantity * p.unitCost),
        0
      );
      const lowStockItems = products.filter(p => {
        const status = getStockLevelStatus(p.currentQuantity, p.parLevel, p.criticalLevel);
        return status === 'low' || status === 'critical' || status === 'out_of_stock';
      }).length;

      return {
        ...vendor,
        totalProductValue,
        lowStockItems
      };
    });
  }

  /**
   * Update vendor product count
   */
  private async updateVendorProductCount(vendorId: string, change: number): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTIONS.VENDORS, vendorId), {
        productCount: increment(change),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating vendor product count:', error);
    }
  }

  // ============================================================================
  // INVENTORY TRANSACTIONS
  // ============================================================================

  /**
   * Adjust product quantity with full audit trail
   */
  async adjustQuantity(
    productId: string,
    quantityChange: number,
    type: InventoryTransactionType,
    userId: string,
    userName: string,
    options?: {
      reason?: string;
      notes?: string;
      referenceId?: string;
      referenceType?: 'sale' | 'purchase_order' | 'count_session' | 'manual';
      barberId?: string;    // back-bar use: who used it
      barberName?: string;
    }
  ): Promise<InventoryTransaction> {
    const product = await this.getProduct(productId);
    if (!product) {
      throw new Error(`Product not found: ${productId}`);
    }

    const previousQuantity = product.currentQuantity;
    const newQuantity = Math.max(0, previousQuantity + quantityChange);

    // Update product quantity
    await updateDoc(doc(db, COLLECTIONS.PRODUCTS, productId), {
      currentQuantity: newQuantity,
      updatedAt: serverTimestamp()
    });

    // Create transaction record
    const transactionData = {
      productId,
      productName: product.name,
      productBarcode: product.barcode || null,
      type,
      quantity: quantityChange, // Deprecated but kept for compatibility
      quantityChange,
      previousQuantity,
      newQuantity,
      referenceId: options?.referenceId || null,
      referenceType: options?.referenceType || 'manual',
      reason: options?.reason || null,
      notes: options?.notes || null,
      createdBy: userId, // Deprecated but kept for compatibility
      userId,
      userName,
      barberId: options?.barberId || null,
      barberName: options?.barberName || null,
      createdAt: serverTimestamp()
    };

    const txRef = await addDoc(collection(db, COLLECTIONS.TRANSACTIONS), transactionData);

    console.log(`✓ Inventory adjusted: ${product.name} ${quantityChange > 0 ? '+' : ''}${quantityChange} (${previousQuantity} → ${newQuantity})`);

    // Check if below par level and add to replenishment queue
    if (newQuantity <= product.parLevel && previousQuantity > product.parLevel) {
      const trigger: ReplenishmentTrigger = type === 'sale' ? 'sale' : 'par_alert';
      await this.addToReplenishmentQueue({ ...product, currentQuantity: newQuantity }, trigger);
    }

    // Check for critical stock notification
    if (newQuantity <= product.criticalLevel) {
      await this.createStockNotification(product, newQuantity);
    }

    return {
      id: txRef.id,
      productId,
      productName: product.name,
      productBarcode: product.barcode,
      type,
      quantity: quantityChange,
      quantityChange,
      previousQuantity,
      newQuantity,
      referenceId: options?.referenceId,
      referenceType: options?.referenceType,
      reason: options?.reason,
      notes: options?.notes,
      createdBy: userId,
      userId,
      userName,
      createdAt: new Date()
    };
  }

  /**
   * Quick quantity adjustment (commonly used with scanner)
   */
  async quickAdjust(
    productId: string,
    amount: number,
    type: 'add' | 'remove' | 'set',
    userId: string,
    userName: string,
    reason?: string
  ): Promise<InventoryTransaction> {
    const product = await this.getProduct(productId);
    if (!product) {
      throw new Error(`Product not found: ${productId}`);
    }

    let quantityChange: number;
    let adjustmentType: InventoryTransactionType = 'adjustment';
    let adjustmentReason: string;

    if (type === 'set') {
      quantityChange = amount - product.currentQuantity;
      adjustmentReason = reason || `Set quantity to ${amount}`;
    } else if (type === 'add') {
      quantityChange = amount;
      adjustmentType = 'restock';
      adjustmentReason = reason || `Added ${amount} units`;
    } else {
      quantityChange = -amount;
      adjustmentReason = reason || `Removed ${amount} units`;
    }

    return this.adjustQuantity(productId, quantityChange, adjustmentType, userId, userName, {
      reason: adjustmentReason
    });
  }

  /**
   * Decrement quantity for a sale (called from POS)
   */
  async decrementForSale(
    productId: string,
    quantity: number,
    saleId: string,
    userId: string,
    userName: string
  ): Promise<InventoryTransaction> {
    return this.adjustQuantity(productId, -quantity, 'sale', userId, userName, {
      referenceId: saleId,
      referenceType: 'sale',
      reason: 'Sold to customer'
    });
  }

  /**
   * Increment quantity for restock (receiving inventory)
   */
  async incrementForRestock(
    productId: string,
    quantity: number,
    purchaseOrderId: string | undefined,
    userId: string,
    userName: string
  ): Promise<InventoryTransaction> {
    return this.adjustQuantity(productId, quantity, 'restock', userId, userName, {
      referenceId: purchaseOrderId,
      referenceType: purchaseOrderId ? 'purchase_order' : 'manual',
      reason: 'Restocked inventory'
    });
  }

  /**
   * Record BACK-BAR USE — a product used in service (not sold retail). Deducts stock as a 'back_bar'
   * transaction ATTRIBUTED TO A BARBER (per-barber professional-use cost). (Jose 2026-09-25)
   */
  async recordBackBarUse(
    productId: string,
    quantity: number,
    barberId: string,
    barberName: string,
    userId: string,
    userName: string
  ): Promise<InventoryTransaction> {
    return this.adjustQuantity(productId, -Math.abs(quantity), 'back_bar', userId, userName, {
      referenceType: 'manual',
      reason: 'Back-bar use',
      barberId,
      barberName,
    });
  }

  /**
   * Get recent transactions
   */
  async getRecentTransactions(limitCount = 50): Promise<InventoryTransaction[]> {
    const q = query(
      collection(db, COLLECTIONS.TRANSACTIONS),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.mapTransactionDoc(docSnap));
  }

  /**
   * Get transactions with filtering
   */
  async getFilteredTransactions(filters: TransactionFilterOptions): Promise<InventoryTransaction[]> {
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];

    if (filters.productId) {
      constraints.push(where('productId', '==', filters.productId));
    }

    if (filters.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }

    if (filters.type && filters.type !== 'all') {
      constraints.push(where('type', '==', filters.type));
    }

    if (filters.limit) {
      constraints.push(limit(filters.limit));
    }

    const q = query(collection(db, COLLECTIONS.TRANSACTIONS), ...constraints);
    const snapshot = await getDocs(q);
    let transactions = snapshot.docs.map(docSnap => this.mapTransactionDoc(docSnap));

    // Apply date filters (client-side for now - Firestore limitation with multiple range queries)
    if (filters.startDate) {
      transactions = transactions.filter(t => t.createdAt >= filters.startDate!);
    }

    if (filters.endDate) {
      transactions = transactions.filter(t => t.createdAt <= filters.endDate!);
    }

    // Apply offset
    if (filters.offset) {
      transactions = transactions.slice(filters.offset);
    }

    return transactions;
  }

  /**
   * Get transactions for a specific product
   */
  async getProductTransactions(productId: string, limitCount = 100): Promise<InventoryTransaction[]> {
    return this.getFilteredTransactions({ productId, limit: limitCount });
  }

  /**
   * Export transactions to CSV-friendly format
   */
  async exportTransactions(filters: TransactionFilterOptions): Promise<any[]> {
    const transactions = await this.getFilteredTransactions(filters);

    return transactions.map(t => ({
      date: t.createdAt.toISOString(),
      productName: t.productName,
      barcode: t.productBarcode || '',
      transactionType: t.type,
      quantityChange: t.quantityChange,
      previousQuantity: t.previousQuantity,
      newQuantity: t.newQuantity,
      userName: t.userName,
      reason: t.reason || '',
      notes: t.notes || ''
    }));
  }

  // ============================================================================
  // REPLENISHMENT QUEUE
  // ============================================================================

  /**
   * Add product to replenishment queue
   */
  async addToReplenishmentQueue(
    product: Product,
    triggeredBy: ReplenishmentTrigger = 'manual'
  ): Promise<ReplenishmentQueueItem | null> {
    // Check if already in queue
    const existingQ = query(
      collection(db, COLLECTIONS.QUEUE),
      where('productId', '==', product.id),
      where('status', '==', 'queued')
    );
    const existing = await getDocs(existingQ);

    if (!existing.empty) {
      console.log('Product already in replenishment queue:', product.name);
      return null;
    }

    const suggestedQuantity = calculateSuggestedOrderQuantity(
      product.currentQuantity,
      product.parLevel,
      product.reorderQuantity
    );
    const estimatedCost = suggestedQuantity * product.unitCost;

    const queueData = {
      productId: product.id,
      productName: product.name,
      productBarcode: product.barcode || null,
      vendorId: product.vendorId,
      vendorName: product.vendorName,
      currentQuantity: product.currentQuantity,
      parLevel: product.parLevel,
      suggestedOrderQuantity: suggestedQuantity,
      unitCost: product.unitCost,
      estimatedCost,
      triggeredAt: serverTimestamp(),
      triggeredBy,
      status: 'queued' as ReplenishmentStatus,
      purchaseOrderId: null,
      notes: null
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.QUEUE), queueData);
    console.log('🔔 Added to replenishment queue:', product.name);

    const result: ReplenishmentQueueItem = {
      id: docRef.id,
      productId: product.id,
      productName: product.name,
      productBarcode: product.barcode,
      vendorId: product.vendorId,
      vendorName: product.vendorName,
      currentQuantity: product.currentQuantity,
      parLevel: product.parLevel,
      suggestedOrderQuantity: suggestedQuantity,
      unitCost: product.unitCost,
      estimatedCost,
      triggeredAt: new Date(),
      triggeredBy,
      status: 'queued',
      purchaseOrderId: undefined,
      notes: undefined
    };

    return result;
  }

  /**
   * Remove item from replenishment queue
   */
  async removeFromQueue(queueItemId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.QUEUE, queueItemId));
    console.log('✓ Removed from replenishment queue:', queueItemId);
  }

  /**
   * Update queue item status
   */
  async updateQueueItemStatus(
    queueItemId: string,
    status: ReplenishmentStatus,
    purchaseOrderId?: string
  ): Promise<void> {
    const updateData: any = { status };
    if (purchaseOrderId) {
      updateData.purchaseOrderId = purchaseOrderId;
    }

    await updateDoc(doc(db, COLLECTIONS.QUEUE, queueItemId), updateData);
  }

  /**
   * Get all queued items (from cache)
   */
  getReplenishmentQueue(): ReplenishmentQueueItem[] {
    return this.cachedQueueItems;
  }

  /**
   * Get replenishment queue grouped by vendor
   */
  getReplenishmentQueueByVendor(): VendorQueueGroup[] {
    const grouped = new Map<string, ReplenishmentQueueItem[]>();

    this.cachedQueueItems.forEach(item => {
      const existing = grouped.get(item.vendorId) || [];
      existing.push(item);
      grouped.set(item.vendorId, existing);
    });

    const result: VendorQueueGroup[] = [];
    grouped.forEach((items, vendorId) => {
      const vendor = this.cachedVendors.find(v => v.id === vendorId);
      result.push({
        vendorId,
        vendorName: vendor?.name || items[0]?.vendorName || 'Unknown Vendor',
        items,
        totalEstimatedCost: items.reduce((sum, item) => sum + item.estimatedCost, 0),
        itemCount: items.length
      });
    });

    return result.sort((a, b) => b.itemCount - a.itemCount);
  }

  /**
   * Manually add product to queue
   */
  async manuallyQueueProduct(productId: string): Promise<ReplenishmentQueueItem | null> {
    const product = await this.getProduct(productId);
    if (!product) {
      throw new Error(`Product not found: ${productId}`);
    }
    return this.addToReplenishmentQueue(product, 'manual');
  }

  // ============================================================================
  // PURCHASE ORDERS
  // ============================================================================

  /**
   * Create a purchase order from queue items
   */
  async createPurchaseOrder(
    vendorId: string,
    queueItemIds: string[],
    createdBy: string,
    createdByName: string,
    notes?: string
  ): Promise<PurchaseOrder> {
    const vendor = await this.getVendor(vendorId);
    if (!vendor) {
      throw new Error(`Vendor not found: ${vendorId}`);
    }

    // Get queue items and build PO items
    const queueItems = this.cachedQueueItems.filter(item => queueItemIds.includes(item.id));
    if (queueItems.length === 0) {
      throw new Error('No valid queue items found');
    }

    // Get existing PO count for order number
    const existingPOsQuery = query(collection(db, COLLECTIONS.PURCHASE_ORDERS));
    const existingPOs = await getDocs(existingPOsQuery);
    const orderNumber = generateOrderNumber(existingPOs.size);

    const poItems: PurchaseOrderItem[] = queueItems.map(qItem => ({
      id: qItem.id,
      productId: qItem.productId,
      productName: qItem.productName,
      productBarcode: qItem.productBarcode,
      quantityOrdered: qItem.suggestedOrderQuantity,
      quantityReceived: 0,
      unitCost: qItem.unitCost,
      totalCost: qItem.suggestedOrderQuantity * qItem.unitCost,
      isReceived: false
    }));

    const totalCost = poItems.reduce((sum, item) => sum + item.totalCost, 0);

    // Calculate expected delivery date based on vendor lead time
    const expectedDeliveryDate = new Date();
    expectedDeliveryDate.setDate(expectedDeliveryDate.getDate() + (vendor.leadTimeDays || 7));

    const poData = {
      orderNumber,
      vendorId,
      vendorName: vendor.name,
      items: poItems,
      totalCost,
      status: 'draft' as PurchaseOrderStatus,
      createdAt: serverTimestamp(),
      createdBy,
      createdByName,
      approvedAt: null,
      approvedBy: null,
      approvedByName: null,
      orderedAt: null,
      orderMethod: 'manual' as const,
      expectedDeliveryDate: Timestamp.fromDate(expectedDeliveryDate),
      receivedAt: null,
      notes: notes || null,
      orderConfirmation: null
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.PURCHASE_ORDERS), poData);

    // Update queue items to 'in_order' status
    const batch = writeBatch(db);
    queueItemIds.forEach(itemId => {
      batch.update(doc(db, COLLECTIONS.QUEUE, itemId), {
        status: 'in_order',
        purchaseOrderId: docRef.id
      });
    });
    await batch.commit();

    // Update vendor pending orders count
    await updateDoc(doc(db, COLLECTIONS.VENDORS, vendorId), {
      pendingOrdersCount: increment(1),
      updatedAt: serverTimestamp()
    });

    console.log('✓ Purchase order created:', orderNumber);

    return {
      id: docRef.id,
      orderNumber,
      vendorId,
      vendorName: vendor.name,
      items: poItems,
      totalCost,
      status: 'draft',
      createdAt: new Date(),
      createdBy,
      createdByName,
      orderMethod: 'manual',
      expectedDeliveryDate,
      notes
    };
  }

  /**
   * Create PO directly from form data (not from queue)
   */
  async createPurchaseOrderDirect(
    data: PurchaseOrderFormData,
    createdBy: string,
    createdByName: string
  ): Promise<PurchaseOrder> {
    const vendor = await this.getVendor(data.vendorId);
    if (!vendor) {
      throw new Error(`Vendor not found: ${data.vendorId}`);
    }

    // Get existing PO count for order number
    const existingPOsQuery = query(collection(db, COLLECTIONS.PURCHASE_ORDERS));
    const existingPOs = await getDocs(existingPOsQuery);
    const orderNumber = generateOrderNumber(existingPOs.size);

    // Build PO items from form data
    const poItems: PurchaseOrderItem[] = [];
    for (const item of data.items) {
      const product = await this.getProduct(item.productId);
      if (!product) continue;

      poItems.push({
        id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        productId: item.productId,
        productName: product.name,
        productBarcode: product.barcode,
        quantityOrdered: item.quantityOrdered,
        quantityReceived: 0,
        unitCost: product.unitCost,
        totalCost: item.quantityOrdered * product.unitCost,
        isReceived: false
      });
    }

    const totalCost = poItems.reduce((sum, item) => sum + item.totalCost, 0);

    const expectedDeliveryDate = new Date();
    expectedDeliveryDate.setDate(expectedDeliveryDate.getDate() + (vendor.leadTimeDays || 7));

    const poData = {
      orderNumber,
      vendorId: data.vendorId,
      vendorName: vendor.name,
      items: poItems,
      totalCost,
      status: 'draft' as PurchaseOrderStatus,
      createdAt: serverTimestamp(),
      createdBy,
      createdByName,
      orderMethod: 'manual' as const,
      expectedDeliveryDate: Timestamp.fromDate(expectedDeliveryDate),
      notes: data.notes || null
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.PURCHASE_ORDERS), poData);

    // Update vendor pending orders count
    await updateDoc(doc(db, COLLECTIONS.VENDORS, data.vendorId), {
      pendingOrdersCount: increment(1),
      updatedAt: serverTimestamp()
    });

    console.log('✓ Purchase order created:', orderNumber);

    return {
      id: docRef.id,
      orderNumber,
      vendorId: data.vendorId,
      vendorName: vendor.name,
      items: poItems,
      totalCost,
      status: 'draft',
      createdAt: new Date(),
      createdBy,
      createdByName,
      orderMethod: 'manual',
      expectedDeliveryDate,
      notes: data.notes
    };
  }

  /**
   * Update purchase order status
   */
  async updatePurchaseOrderStatus(
    poId: string,
    status: PurchaseOrderStatus,
    userId?: string,
    userName?: string,
    orderConfirmation?: string
  ): Promise<void> {
    const updateData: any = { status };

    if (status === 'approved' && userId && userName) {
      updateData.approvedAt = serverTimestamp();
      updateData.approvedBy = userId;
      updateData.approvedByName = userName;
    }

    if (status === 'ordered') {
      updateData.orderedAt = serverTimestamp();
      if (orderConfirmation) {
        updateData.orderConfirmation = orderConfirmation;
      }

      // Update queue items to 'ordered' status
      const po = await this.getPurchaseOrder(poId);
      if (po) {
        const batch = writeBatch(db);
        for (const item of po.items) {
          const queueQuery = query(
            collection(db, COLLECTIONS.QUEUE),
            where('productId', '==', item.productId),
            where('purchaseOrderId', '==', poId)
          );
          const queueDocs = await getDocs(queueQuery);
          queueDocs.forEach(qDoc => {
            batch.update(qDoc.ref, { status: 'ordered' });
          });
        }
        await batch.commit();
      }
    }

    await updateDoc(doc(db, COLLECTIONS.PURCHASE_ORDERS, poId), updateData);
    console.log('✓ Purchase order status updated:', status);
  }

  /**
   * Receive items from a purchase order
   */
  async receivePurchaseOrder(
    poId: string,
    receivedItems: Array<{ productId: string; quantityReceived: number }>,
    userId: string,
    userName: string
  ): Promise<void> {
    const po = await this.getPurchaseOrder(poId);
    if (!po) {
      throw new Error(`Purchase order not found: ${poId}`);
    }

    // Update each item and product quantity
    const updatedItems = po.items.map(item => {
      const received = receivedItems.find(r => r.productId === item.productId);
      if (received) {
        return {
          ...item,
          quantityReceived: item.quantityReceived + received.quantityReceived,
          isReceived: (item.quantityReceived + received.quantityReceived) >= item.quantityOrdered
        };
      }
      return item;
    });

    // Update product quantities via restock
    for (const received of receivedItems) {
      await this.incrementForRestock(
        received.productId,
        received.quantityReceived,
        poId,
        userId,
        userName
      );

      // Update product's lastOrderedAt
      await updateDoc(doc(db, COLLECTIONS.PRODUCTS, received.productId), {
        lastOrderedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update queue item to 'received' and remove from queue
      const queueQuery = query(
        collection(db, COLLECTIONS.QUEUE),
        where('productId', '==', received.productId),
        where('purchaseOrderId', '==', poId)
      );
      const queueDocs = await getDocs(queueQuery);
      // M10 FIX (Jose 2026-10-29 audit): was `forEach(async … await …)` — forEach does NOT await its
      // async callbacks, so the queue→'received' updates were fire-and-forget. If one failed in flight,
      // the PO below could be marked 'received' while a queue item stayed 'queued' forever (state drift).
      // Await them all so the queue is reconciled BEFORE the PO status is written.
      await Promise.all(queueDocs.docs.map(qDoc => updateDoc(qDoc.ref, { status: 'received' })));
    }

    // Check if all items received
    const allReceived = updatedItems.every(item => item.isReceived);
    const anyReceived = updatedItems.some(item => item.quantityReceived > 0);

    let newStatus: PurchaseOrderStatus = po.status;
    if (allReceived) {
      newStatus = 'received';
    } else if (anyReceived) {
      newStatus = 'partial_received';
    }

    const updateData: any = {
      items: updatedItems,
      status: newStatus
    };

    if (newStatus === 'received') {
      updateData.receivedAt = serverTimestamp();

      // Decrease vendor pending orders count
      await updateDoc(doc(db, COLLECTIONS.VENDORS, po.vendorId), {
        pendingOrdersCount: increment(-1),
        updatedAt: serverTimestamp()
      });
    }

    await updateDoc(doc(db, COLLECTIONS.PURCHASE_ORDERS, poId), updateData);
    console.log('✓ Purchase order items received');
  }

  /**
   * Get a single purchase order
   */
  async getPurchaseOrder(poId: string): Promise<PurchaseOrder | null> {
    const docSnap = await getDoc(doc(db, COLLECTIONS.PURCHASE_ORDERS, poId));
    if (!docSnap.exists()) return null;
    return this.mapPurchaseOrderDoc(docSnap);
  }

  /**
   * Get all purchase orders
   */
  async getAllPurchaseOrders(): Promise<PurchaseOrder[]> {
    const q = query(
      collection(db, COLLECTIONS.PURCHASE_ORDERS),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.mapPurchaseOrderDoc(docSnap));
  }

  /**
   * Get purchase orders by status
   */
  async getPurchaseOrdersByStatus(status: PurchaseOrderStatus): Promise<PurchaseOrder[]> {
    const q = query(
      collection(db, COLLECTIONS.PURCHASE_ORDERS),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.mapPurchaseOrderDoc(docSnap));
  }

  /**
   * Get purchase orders by vendor
   */
  async getPurchaseOrdersByVendor(vendorId: string): Promise<PurchaseOrder[]> {
    const q = query(
      collection(db, COLLECTIONS.PURCHASE_ORDERS),
      where('vendorId', '==', vendorId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.mapPurchaseOrderDoc(docSnap));
  }

  /**
   * Get pending purchase orders count
   */
  async getPendingPurchaseOrdersCount(): Promise<number> {
    const q = query(
      collection(db, COLLECTIONS.PURCHASE_ORDERS),
      where('status', 'in', ['draft', 'pending_approval', 'approved', 'ordered', 'partial_received'])
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }

  /**
   * Cancel a purchase order
   */
  async cancelPurchaseOrder(poId: string): Promise<void> {
    const po = await this.getPurchaseOrder(poId);
    if (!po) {
      throw new Error(`Purchase order not found: ${poId}`);
    }

    // Update queue items back to 'queued' status
    const batch = writeBatch(db);
    for (const item of po.items) {
      const queueQuery = query(
        collection(db, COLLECTIONS.QUEUE),
        where('productId', '==', item.productId),
        where('purchaseOrderId', '==', poId)
      );
      const queueDocs = await getDocs(queueQuery);
      queueDocs.forEach(qDoc => {
        batch.update(qDoc.ref, {
          status: 'queued',
          purchaseOrderId: null
        });
      });
    }
    await batch.commit();

    // Update PO status
    await updateDoc(doc(db, COLLECTIONS.PURCHASE_ORDERS, poId), {
      status: 'cancelled'
    });

    // Decrease vendor pending orders count
    await updateDoc(doc(db, COLLECTIONS.VENDORS, po.vendorId), {
      pendingOrdersCount: increment(-1),
      updatedAt: serverTimestamp()
    });

    console.log('✓ Purchase order cancelled:', po.orderNumber);
  }

  // ============================================================================
  // INVENTORY COUNT SESSIONS
  // ============================================================================

  /**
   * Start a new inventory count session
   */
  async startCountSession(
    userId: string,
    userName: string,
    notes?: string
  ): Promise<InventoryCountSession> {
    const products = this.getAllProducts();

    const countItems: InventoryCountItem[] = products.map(product => ({
      id: product.id,
      productId: product.id,
      productName: product.name,
      barcode: product.barcode,
      category: product.category,
      expectedQuantity: product.currentQuantity,
      countedQuantity: 0,
      variance: 0,
      varianceValue: 0,
      unitCost: product.unitCost,
      isCounted: false
    }));

    const sessionData = {
      status: 'in_progress' as CountSessionStatus,
      startedAt: serverTimestamp(),
      startedBy: userId,
      startedByName: userName,
      completedAt: null,
      completedBy: null,
      itemsExpected: products.length,
      itemsCounted: 0,
      items: countItems,
      varianceReport: null,
      notes: notes || null
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.COUNT_SESSIONS), sessionData);
    console.log('✓ Inventory count session started');

    const result: InventoryCountSession = {
      id: docRef.id,
      status: 'in_progress',
      startedAt: new Date(),
      startedBy: userId,
      startedByName: userName,
      completedAt: undefined,
      completedBy: undefined,
      itemsExpected: products.length,
      itemsCounted: 0,
      items: countItems,
      varianceReport: undefined,
      notes
    };

    return result;
  }

  /**
   * Record a count for an item in the session
   */
  async recordCount(
    sessionId: string,
    productId: string,
    countedQuantity: number,
    adjustmentNote?: string
  ): Promise<void> {
    const sessionDoc = await getDoc(doc(db, COLLECTIONS.COUNT_SESSIONS, sessionId));
    if (!sessionDoc.exists()) {
      throw new Error(`Count session not found: ${sessionId}`);
    }

    const session = this.mapCountSessionDoc(sessionDoc);
    const itemIndex = session.items.findIndex(item => item.productId === productId);

    if (itemIndex === -1) {
      throw new Error(`Product not found in count session: ${productId}`);
    }

    const item = session.items[itemIndex];
    const variance = countedQuantity - item.expectedQuantity;
    const varianceValue = variance * item.unitCost;

    const updatedItem: InventoryCountItem = {
      ...item,
      countedQuantity,
      variance,
      varianceValue,
      isCounted: true,
      countedAt: new Date(),
      adjustmentNote: adjustmentNote || item.adjustmentNote
    };

    const updatedItems = [...session.items];
    updatedItems[itemIndex] = updatedItem;

    const itemsCounted = updatedItems.filter(i => i.isCounted).length;

    await updateDoc(doc(db, COLLECTIONS.COUNT_SESSIONS, sessionId), {
      items: updatedItems,
      itemsCounted
    });

    console.log(`✓ Recorded count for ${item.productName}: ${countedQuantity} (variance: ${variance})`);
  }

  /**
   * Complete a count session and generate variance report
   */
  async completeCountSession(
    sessionId: string,
    userId: string,
    applyCorrections: boolean = true
  ): Promise<VarianceReport> {
    const sessionDoc = await getDoc(doc(db, COLLECTIONS.COUNT_SESSIONS, sessionId));
    if (!sessionDoc.exists()) {
      throw new Error(`Count session not found: ${sessionId}`);
    }

    const session = this.mapCountSessionDoc(sessionDoc);

    // Generate variance report
    const discrepancies: VarianceDiscrepancy[] = session.items
      .filter(item => item.isCounted && item.variance !== 0)
      .map(item => ({
        productId: item.productId,
        productName: item.productName,
        barcode: item.barcode,
        expectedQuantity: item.expectedQuantity,
        countedQuantity: item.countedQuantity,
        variance: item.variance,
        varianceValue: item.varianceValue,
        adjustmentNote: item.adjustmentNote
      }));

    const itemsWithPositiveVariance = discrepancies.filter(d => d.variance > 0).length;
    const itemsWithNegativeVariance = discrepancies.filter(d => d.variance < 0).length;
    const totalVarianceUnits = discrepancies.reduce((sum, d) => sum + d.variance, 0);
    const totalVarianceValue = discrepancies.reduce((sum, d) => sum + d.varianceValue, 0);

    const varianceReport: VarianceReport = {
      sessionId,
      totalItems: session.itemsExpected,
      itemsCounted: session.itemsCounted,
      itemsWithVariance: discrepancies.length,
      itemsWithPositiveVariance,
      itemsWithNegativeVariance,
      totalVarianceUnits,
      totalVarianceValue,
      generatedAt: new Date(),
      generatedBy: userId,
      discrepancies
    };

    // Apply corrections if requested
    if (applyCorrections) {
      const user = session.startedByName; // Use session creator name

      for (const item of session.items) {
        if (item.isCounted && item.variance !== 0) {
          await this.adjustQuantity(
            item.productId,
            item.variance,
            'count_correction',
            userId,
            user,
            {
              referenceId: sessionId,
              referenceType: 'count_session',
              reason: `Inventory count correction`,
              notes: item.adjustmentNote || `Expected: ${item.expectedQuantity}, Counted: ${item.countedQuantity}`
            }
          );

          // Update lastCountedAt
          await updateDoc(doc(db, COLLECTIONS.PRODUCTS, item.productId), {
            lastCountedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      }
    }

    // Update session as completed
    await updateDoc(doc(db, COLLECTIONS.COUNT_SESSIONS, sessionId), {
      status: 'completed',
      completedAt: serverTimestamp(),
      completedBy: userId,
      varianceReport
    });

    console.log('✓ Inventory count session completed');
    return varianceReport;
  }

  /**
   * Cancel a count session
   */
  async cancelCountSession(sessionId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.COUNT_SESSIONS, sessionId), {
      status: 'cancelled'
    });
    console.log('✓ Inventory count session cancelled');
  }

  /**
   * Get a count session
   */
  async getCountSession(sessionId: string): Promise<InventoryCountSession | null> {
    const docSnap = await getDoc(doc(db, COLLECTIONS.COUNT_SESSIONS, sessionId));
    if (!docSnap.exists()) return null;
    return this.mapCountSessionDoc(docSnap);
  }

  /**
   * Get active count sessions
   */
  async getActiveCountSessions(): Promise<InventoryCountSession[]> {
    const q = query(
      collection(db, COLLECTIONS.COUNT_SESSIONS),
      where('status', '==', 'in_progress'),
      orderBy('startedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.mapCountSessionDoc(docSnap));
  }

  /**
   * Get completed count sessions
   */
  async getCompletedCountSessions(limitCount = 10): Promise<InventoryCountSession[]> {
    const q = query(
      collection(db, COLLECTIONS.COUNT_SESSIONS),
      where('status', '==', 'completed'),
      orderBy('completedAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.mapCountSessionDoc(docSnap));
  }

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  /**
   * Create stock notification
   */
  private async createStockNotification(product: Product, currentQuantity: number): Promise<void> {
    let notificationType: 'low_stock' | 'critical_stock' | 'out_of_stock';
    let priority: NotificationPriority;
    let title: string;
    let message: string;

    if (currentQuantity === 0) {
      notificationType = 'out_of_stock';
      priority = 'critical';
      title = `Out of Stock: ${product.name}`;
      message = `${product.name} is now out of stock! Add to your next order.`;
    } else if (currentQuantity <= product.criticalLevel) {
      notificationType = 'critical_stock';
      priority = 'high';
      title = `Critical Stock: ${product.name}`;
      message = `${product.name} is at ${currentQuantity} units (critical level: ${product.criticalLevel})`;
    } else {
      notificationType = 'low_stock';
      priority = 'medium';
      title = `Low Stock: ${product.name}`;
      message = `${product.name} is at ${currentQuantity} units (par level: ${product.parLevel})`;
    }

    const notificationData = {
      type: notificationType,
      priority,
      title,
      message,
      productId: product.id,
      productName: product.name,
      isRead: false,
      createdAt: serverTimestamp()
    };

    await addDoc(collection(db, COLLECTIONS.NOTIFICATIONS), notificationData);
    console.log(`🔔 Stock notification created: ${title}`);
  }

  /**
   * Get unread notifications
   */
  async getUnreadNotifications(): Promise<InventoryNotification[]> {
    const q = query(
      collection(db, COLLECTIONS.NOTIFICATIONS),
      where('isRead', '==', false),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        type: data.type,
        priority: data.priority,
        title: data.title,
        message: data.message,
        productId: data.productId,
        productName: data.productName,
        purchaseOrderId: data.purchaseOrderId,
        isRead: data.isRead,
        createdAt: data.createdAt?.toDate() || new Date(),
        readAt: data.readAt?.toDate()
      };
    });
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId), {
      isRead: true,
      readAt: serverTimestamp()
    });
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsAsRead(): Promise<void> {
    const notifications = await this.getUnreadNotifications();
    const batch = writeBatch(db);

    notifications.forEach(notification => {
      batch.update(doc(db, COLLECTIONS.NOTIFICATIONS, notification.id), {
        isRead: true,
        readAt: serverTimestamp()
      });
    });

    await batch.commit();
  }

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get comprehensive inventory statistics
   */
  getStats(): InventoryStats {
    const products = this.getAllProducts();
    const productsWithStatus = this.getProductsWithStatus();

    const lowStockCount = productsWithStatus.filter(p => p.stockStatus === 'low').length;
    const criticalStockCount = productsWithStatus.filter(p => p.stockStatus === 'critical').length;
    const outOfStockCount = productsWithStatus.filter(p => p.stockStatus === 'out_of_stock').length;

    const totalInventoryValue = products.reduce(
      (sum, p) => sum + (p.currentQuantity * p.unitCost),
      0
    );

    return {
      totalProducts: products.length,
      activeProducts: products.filter(p => p.isActive).length,
      lowStockCount,
      criticalStockCount,
      outOfStockCount,
      totalInventoryValue,
      activeVendors: this.getAllVendors().length,
      pendingOrdersCount: this.cachedVendors.reduce((sum, v) => sum + v.pendingOrdersCount, 0),
      queuedItemsCount: this.cachedQueueItems.length
    };
  }

  /**
   * Get inventory value by category
   */
  getInventoryValueByCategory(): Record<ProductCategory, number> {
    const products = this.getAllProducts();
    const result: Record<ProductCategory, number> = {
      hair: 0,
      styling: 0,
      tools: 0,
      supplies: 0,
      other: 0
    };

    products.forEach(product => {
      result[product.category] += product.currentQuantity * product.unitCost;
    });

    return result;
  }

  /**
   * Get inventory value by vendor
   */
  getInventoryValueByVendor(): Array<{ vendorId: string; vendorName: string; value: number }> {
    const products = this.getAllProducts();
    const valueMap = new Map<string, { name: string; value: number }>();

    products.forEach(product => {
      const current = valueMap.get(product.vendorId) || { name: product.vendorName, value: 0 };
      current.value += product.currentQuantity * product.unitCost;
      valueMap.set(product.vendorId, current);
    });

    return Array.from(valueMap.entries())
      .map(([vendorId, data]) => ({
        vendorId,
        vendorName: data.name,
        value: data.value
      }))
      .sort((a, b) => b.value - a.value);
  }

  // ============================================================================
  // SUBSCRIPTIONS
  // ============================================================================

  /**
   * Subscribe to product updates
   */
  subscribeToProducts(callback: (products: Product[]) => void): () => void {
    this.productSubscribers.push(callback);
    callback(this.cachedProducts);
    return () => {
      this.productSubscribers = this.productSubscribers.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to vendor updates
   */
  subscribeToVendors(callback: (vendors: Vendor[]) => void): () => void {
    this.vendorSubscribers.push(callback);
    callback(this.cachedVendors);
    return () => {
      this.vendorSubscribers = this.vendorSubscribers.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to replenishment queue updates
   */
  subscribeToQueue(callback: (items: ReplenishmentQueueItem[]) => void): () => void {
    this.queueSubscribers.push(callback);
    callback(this.cachedQueueItems);
    return () => {
      this.queueSubscribers = this.queueSubscribers.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to stats updates
   */
  subscribeToStats(callback: (stats: InventoryStats) => void): () => void {
    this.statsSubscribers.push(callback);
    callback(this.getStats());
    return () => {
      this.statsSubscribers = this.statsSubscribers.filter(cb => cb !== callback);
    };
  }

  private notifyProductSubscribers(): void {
    this.productSubscribers.forEach(callback => callback(this.cachedProducts));
  }

  private notifyVendorSubscribers(): void {
    this.vendorSubscribers.forEach(callback => callback(this.cachedVendors));
  }

  private notifyQueueSubscribers(): void {
    this.queueSubscribers.forEach(callback => callback(this.cachedQueueItems));
  }

  private notifyStatsSubscribers(): void {
    this.statsSubscribers.forEach(callback => callback(this.getStats()));
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Cleanup all listeners (call when unmounting)
   */
  destroy(): void {
    if (this.unsubscribeProducts) this.unsubscribeProducts();
    if (this.unsubscribeVendors) this.unsubscribeVendors();
    if (this.unsubscribeQueue) this.unsubscribeQueue();
    this.productSubscribers = [];
    this.vendorSubscribers = [];
    this.queueSubscribers = [];
    this.statsSubscribers = [];
    console.log('✓ Inventory service destroyed');
  }
}

// Export singleton instance
export const inventoryService = new InventoryService();
